import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import api from '../../services/api';
import LogoutModal from '../../components/LogoutModal';
import Footer from '../../components/Footer';

interface UserStats {
  accountCreated: string;
  totalTransactions: number;
  lastLoginDate: string;
  accountStatus: string;
}

const UserDashboard: React.FC = () => {
  const { user, logout } = useAuth();
  const [stats, setStats] = useState<UserStats>({
    accountCreated: '',
    totalTransactions: 0,
    lastLoginDate: '',
    accountStatus: 'Active'
  });
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('home');
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    fetchUserData();
  }, []);

  const fetchUserData = async () => {
    try {
      setLoading(true);
      
      // Fetch user profile data
      const profileResponse = await api.get('/auth/profile');
      const profileData = profileResponse.data.data;

      // Update stats with real data from API
      setStats({
        accountCreated: profileData.created_at ? new Date(profileData.created_at).toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'long',
          day: 'numeric'
        }) : 'N/A',
        totalTransactions: 0, // Will be updated when we have transaction endpoint
        lastLoginDate: profileData.last_login ? new Date(profileData.last_login).toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'long',
          day: 'numeric',
          hour: '2-digit',
          minute: '2-digit'
        }) : 'N/A',
        accountStatus: profileData.is_active ? 'Active' : 'Inactive'
      });
    } catch (error) {
      console.error('Error fetching user data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleNavigation = (tabId: string, path: string) => {
    setActiveTab(tabId);
    window.location.href = path;
  };

  const handleLogoutClick = () => {
    setShowLogoutModal(true);
  };

  const handleLogout = async () => {
    try {
      await logout();
      setShowLogoutModal(false);
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  const handleMyProfile = () => {
    window.location.href = '/user/account';
  };

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const sidebarItems = [
    { id: 'home', label: 'Home', icon: '🏠', path: '/user' },
    { id: 'account', label: 'My Account', icon: '👤', path: '/user/account' },
    { id: 'activity', label: 'My Activity', icon: '📊', path: '/user/activity' }
  ];

  return (
    <div style={{ minHeight: '100vh', backgroundColor: '#f5f5f5', fontFamily: 'Arial, sans-serif' }}>
      {/* Sidebar */}
      <div style={{ 
        position: 'fixed', 
        left: 0, 
        top: 0, 
        width: '250px', 
        height: '100vh', 
        backgroundColor: '#fff', 
        borderRight: '1px solid #ddd',
        padding: '20px',
        boxSizing: 'border-box'
      }}>
        <div style={{ marginBottom: '30px' }}>
          <h2 style={{ margin: 0, color: '#333' }}>YuvanApi</h2>
        </div>
        
        <nav>
          {sidebarItems.map(item => (
            <div key={item.id} style={{ marginBottom: '10px' }}>
              <button
                style={{
                  width: '100%',
                  padding: '12px',
                  border: 'none',
                  backgroundColor: activeTab === item.id ? '#007bff' : 'transparent',
                  color: activeTab === item.id ? 'white' : '#333',
                  borderRadius: '5px',
                  cursor: 'pointer',
                  textAlign: 'left',
                  fontSize: '14px'
                }}
                onClick={() => {
                  handleNavigation(item.id, item.path);
                  setIsMobileMenuOpen(false);
                }}
              >
                {item.icon} {item.label}
              </button>
            </div>
          ))}
        </nav>
      </div>

      {/* Main Content */}
      <div style={{ marginLeft: '250px', padding: '20px' }}>
        {/* Header */}
        <div style={{ 
          backgroundColor: 'white', 
          padding: '20px', 
          borderRadius: '8px', 
          marginBottom: '20px',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <h1 style={{ 
              margin: 0, 
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
              fontSize: '28px',
              fontWeight: '700',
              letterSpacing: '-0.5px'
            }}>
              Hi <span style={{ 
                background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
                fontWeight: '800'
              }}>{user?.name || 'User'}</span>, Welcome to <span style={{ 
                background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
                fontWeight: '800'
              }}>YuvanApi</span>!
            </h1>
            <div style={{ display: 'flex', gap: '10px' }}>
              <button 
                style={{
                  padding: '8px 16px',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  backgroundColor: 'white',
                  cursor: 'pointer'
                }}
                onClick={() => window.location.reload()}
              >
                🔄 Refresh
              </button>
              <button 
                style={{
                  padding: '8px 16px',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  backgroundColor: 'white',
                  cursor: 'pointer'
                }}
                onClick={handleMyProfile}
              >
                👤 My Profile
              </button>
              <button 
                style={{
                  padding: '8px 16px',
                  border: '1px solid #dc3545',
                  borderRadius: '4px',
                  backgroundColor: '#dc3545',
                  color: 'white',
                  cursor: 'pointer'
                }}
                onClick={handleLogoutClick}
              >
                🚪 Logout
              </button>
            </div>
          </div>
        </div>

        {/* Dashboard Content */}
        <div>
          {loading ? (
            <div style={{ textAlign: 'center', padding: '40px', fontSize: '18px' }}>Loading...</div>
          ) : (
            <>
              {/* Welcome Card */}
              <div style={{ 
                backgroundColor: 'white', 
                padding: '30px', 
                borderRadius: '8px', 
                marginBottom: '20px',
                boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                textAlign: 'center'
              }}>
                <div style={{ fontSize: '48px', marginBottom: '20px' }}>👋</div>
                <h2 style={{ margin: '0 0 10px 0', color: '#333' }}>Welcome to Your Dashboard</h2>
                <p style={{ margin: 0, color: '#666' }}>Manage your account and view your activity here</p>
              </div>

              {/* Stats Grid */}
              <div style={{ 
                display: 'grid', 
                gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', 
                gap: '20px', 
                marginBottom: '20px' 
              }}>
                {[
                  { icon: '📅', label: 'Account Created', value: stats.accountCreated },
                  { icon: '📊', label: 'Total Transactions', value: stats.totalTransactions },
                  { icon: '🕐', label: 'Last Login', value: stats.lastLoginDate },
                  { icon: stats.accountStatus === 'Active' ? '✅' : '❌', label: 'Account Status', value: stats.accountStatus }
                ].map((stat, index) => (
                  <div key={index} style={{ 
                    backgroundColor: 'white', 
                    padding: '20px', 
                    borderRadius: '8px',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                    textAlign: 'center'
                  }}>
                    <div style={{ fontSize: '24px', marginBottom: '10px' }}>{stat.icon}</div>
                    <div style={{ fontSize: '12px', color: '#666', marginBottom: '5px', textTransform: 'uppercase' }}>
                      {stat.label}
                    </div>
                    <div style={{ fontSize: '18px', fontWeight: 'bold', color: '#333' }}>
                      {stat.value}
                    </div>
                  </div>
                ))}
              </div>

              {/* Quick Actions */}
              <div style={{ 
                backgroundColor: 'white', 
                padding: '20px', 
                borderRadius: '8px', 
                marginBottom: '20px',
                boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
              }}>
                <h3 style={{ margin: '0 0 20px 0', color: '#333' }}>⚡ Quick Actions</h3>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '15px' }}>
                  <button 
                    style={{
                      padding: '15px',
                      border: '1px solid #007bff',
                      borderRadius: '8px',
                      backgroundColor: '#007bff',
                      color: 'white',
                      cursor: 'pointer',
                      textAlign: 'center'
                    }}
                    onClick={() => window.location.href = '/user/account'}
                  >
                    <div style={{ fontSize: '24px', marginBottom: '5px' }}>👤</div>
                    <div style={{ fontWeight: 'bold' }}>View Profile</div>
                    <div style={{ fontSize: '12px', opacity: 0.8 }}>Manage your account</div>
                  </button>
                  <button 
                    style={{
                      padding: '15px',
                      border: '1px solid #28a745',
                      borderRadius: '8px',
                      backgroundColor: '#28a745',
                      color: 'white',
                      cursor: 'pointer',
                      textAlign: 'center'
                    }}
                    onClick={() => window.location.href = '/user/activity'}
                  >
                    <div style={{ fontSize: '24px', marginBottom: '5px' }}>📊</div>
                    <div style={{ fontWeight: 'bold' }}>View Activity</div>
                    <div style={{ fontSize: '12px', opacity: 0.8 }}>Check your history</div>
                  </button>
                  <button 
                    style={{
                      padding: '15px',
                      border: '1px solid #ffc107',
                      borderRadius: '8px',
                      backgroundColor: '#ffc107',
                      color: '#333',
                      cursor: 'pointer',
                      textAlign: 'center'
                    }}
                    onClick={() => window.location.reload()}
                  >
                    <div style={{ fontSize: '24px', marginBottom: '5px' }}>🔄</div>
                    <div style={{ fontWeight: 'bold' }}>Refresh Data</div>
                    <div style={{ fontSize: '12px', opacity: 0.8 }}>Update dashboard</div>
                  </button>
                </div>
              </div>

              {/* Info Section */}
              <div style={{ 
                backgroundColor: 'white', 
                padding: '20px', 
                borderRadius: '8px',
                boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
              }}>
                <h3 style={{ margin: '0 0 20px 0', color: '#333' }}>ℹ️ Account Information</h3>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '15px' }}>
                  {[
                    { icon: '👤', label: 'Name', value: user?.name || 'N/A' },
                    { icon: '📧', label: 'Email', value: user?.email || 'N/A' },
                    { icon: '🎭', label: 'Role', value: user?.role || 'N/A' },
                    { icon: '⭐', label: 'Account Type', value: 'Standard User' }
                  ].map((info, index) => (
                    <div key={index} style={{ 
                      padding: '15px', 
                      border: '1px solid #eee', 
                      borderRadius: '8px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '15px'
                    }}>
                      <div style={{ fontSize: '24px' }}>{info.icon}</div>
                      <div>
                        <div style={{ fontSize: '12px', color: '#666', marginBottom: '5px' }}>
                          {info.label}
                        </div>
                        <div style={{ fontWeight: 'bold', color: '#333' }}>
                          {info.value}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        <Footer />
      </div>

      {/* Modals */}
      <LogoutModal
        isOpen={showLogoutModal}
        onClose={() => setShowLogoutModal(false)}
        onConfirm={handleLogout}
      />
    </div>
  );
};

export default UserDashboard;
